import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { Megaphone, Cake, PartyPopper, CheckCircle2, Loader2, Building2, History, CalendarDays, MapPin, Images, Paperclip } from 'lucide-react';
import MeCompanyRecognitionFeed from '@/components/enduser/MeCompanyRecognitionFeed';
import { AckInbox } from '@/components/shared/AckInbox';
import { CommImageCarousel, COMM_CATEGORY_META } from '@/components/shared/CommImageCarousel';
import { CommAttachments } from '@/components/shared/CommAttachments';

const PRIORITY_CLS = {
  urgent: 'bg-rose-100 text-rose-700 border-rose-300',
  high: 'bg-amber-100 text-amber-700 border-amber-300',
  normal: 'bg-slate-100 text-slate-600 border-slate-300',
};
const MONTHS_ES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

const fmtDate = (d, isES) => {
  if (!d) return null;
  try {
    return new Date(`${String(d).slice(0, 10)}T12:00:00Z`).toLocaleDateString(
      isES ? 'es' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return String(d).slice(0, 10); }
};

function CommMeta({ c, isES }) {
  const cat = COMM_CATEGORY_META[c.category] || COMM_CATEGORY_META.general;
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
      <Badge variant="outline" className={`text-[10px] ${cat.cls}`} data-testid={`me-comm-category-${c.id}`}>{isES ? cat.es : cat.en}</Badge>
      {c.event_date && (
        <span className="inline-flex items-center gap-1" data-testid={`me-comm-event-date-${c.id}`}>
          <CalendarDays className="h-3 w-3" />{fmtDate(c.event_date, isES)}
        </span>
      )}
      {c.location && (
        <span className="inline-flex items-center gap-1" data-testid={`me-comm-location-${c.id}`}>
          <MapPin className="h-3 w-3" />{c.location}
        </span>
      )}
      {(c.images || []).length > 0 && (
        <span className="inline-flex items-center gap-1">
          <Images className="h-3 w-3" />
          {c.images.length} {c.images.length === 1 ? (isES ? 'imagen' : 'image') : (isES ? 'imágenes' : 'images')}
        </span>
      )}
      {(c.attachments || []).length > 0 && (
        <span className="inline-flex items-center gap-1" data-testid={`me-comm-attachments-count-${c.id}`}>
          <Paperclip className="h-3 w-3" />
          {c.attachments.length} {isES ? 'adjunto(s)' : 'attachment(s)'}
        </span>
      )}
    </div>
  );
}

function CommunicationCard({ c, L, isES, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const [att, setAtt] = useState(c.my_attendance);
  const [attCount, setAttCount] = useState(c.attendance_count || 0);
  const [attBusy, setAttBusy] = useState(false);
  const attendance = async (register) => {
    setAttBusy(true);
    try {
      await api.post(`/me/communications/${c.id}/attend${register ? '' : '/cancel'}`);
      setAtt(register ? 'registered' : null);
      setAttCount((n) => Math.max(0, n + (register ? 1 : -1)));
      toast.success(register ? L('Asistencia confirmada', 'Attendance confirmed') : L('Asistencia cancelada', 'Attendance cancelled'));
    } catch (e) { toast.error(formatApiError(e)); } finally { setAttBusy(false); }
  };
  useEffect(() => {
    if (!c.viewed_at) api.post(`/me/communications/${c.id}/view`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/me/communications/${c.id}/confirm`); onConfirm(c.id); toast.success(L('Lectura confirmada', 'Reading confirmed')); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const imgs = c.images || [];
  return (
    <Card className={`overflow-hidden shadow-card ${!c.viewed_at ? 'border-teal-300' : ''}`} data-testid={`me-comm-${c.id}`}>
      {imgs.length > 0 && <CommImageCarousel images={imgs} title={c.title} className="rounded-b-none" />}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm">{c.title}</p>
              <Badge variant="outline" className={`text-[10px] ${PRIORITY_CLS[c.priority] || PRIORITY_CLS.normal}`}>
                {c.priority === 'urgent' ? L('Urgente', 'Urgent') : c.priority === 'high' ? L('Importante', 'Important') : L('Informativa', 'Informational')}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{(c.published_at || '').slice(0, 10)}</p>
          </div>
          {c.confirmed_at && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
        </div>
        <div className="mt-2"><CommMeta c={c} isES={isES} /></div>
        <p className="text-sm mt-2 whitespace-pre-wrap">{c.body}</p>
        {/* CTH-COM-087 — audio playback + PDF access before any acknowledgment */}
        <CommAttachments attachments={c.attachments || []} isES={isES} />
        {c.require_read_confirmation && !c.confirmed_at && (
          <Button size="sm" className="mt-3" onClick={confirm} disabled={busy} data-testid={`me-comm-confirm-${c.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            {L('Confirmar lectura', 'Confirm reading')}
          </Button>
        )}
        {/* V5.0 §12 — event attendance (distinct from read confirmation) */}
        {c.attendance_enabled && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`me-comm-attendance-${c.id}`}>
            {att === 'registered' ? (
              <>
                <Badge className="bg-emerald-600 text-white border-0 text-[10px]">
                  {L('Asistencia confirmada', 'Attendance confirmed')}
                </Badge>
                <Button size="sm" variant="outline" disabled={attBusy} onClick={() => attendance(false)}
                        data-testid={`me-comm-attend-cancel-${c.id}`}>
                  {L('Cancelar asistencia', 'Cancel attendance')}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" disabled={attBusy} onClick={() => attendance(true)}
                      data-testid={`me-comm-attend-${c.id}`}>
                {attBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PartyPopper className="h-4 w-4 mr-1.5" />}
                {L('Confirmar asistencia', 'Confirm attendance')}
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              {attCount} {L('registrados', 'registered')}
              {c.attendance_capacity ? ` / ${c.attendance_capacity}` : ''}
              {c.attendance_deadline ? ` · ${L('cierre', 'deadline')} ${c.attendance_deadline}` : ''}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function MeCompany() {
  const { i18n } = useTranslation();
  const { user } = useApp();
  const isES = !(i18n.language || '').startsWith('en');
  const L = (es, en) => (isES ? es : en);
  const [comms, setComms] = useState([]);
  const [celebs, setCelebs] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [congrat, setCongrat] = useState(null); // {user_id, name}
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get('/me/communications').then((r) => setComms(r.data || [])).catch(() => {});
    api.get('/me/theart/celebrations').then((r) => setCelebs(r.data)).catch(() => setCelebs({ birthdays: [] }));
  };
  useEffect(load, []);

  const active = comms.filter((c) => !c.expired);
  const history = comms.filter((c) => c.expired);

  const sendCongrats = async () => {
    if (message.trim().length < 2) return;
    setSending(true);
    try {
      await api.post('/client/theart/recognitions', {
        to_user_id: congrat.user_id, type: 'peer', category: 'kindness',
        message: message.trim(), visibility: 'public',
      });
      toast.success(L('¡Felicitación enviada!', 'Congratulations sent!'));
      setCongrat(null); setMessage('');
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="me-company-page">
      <div className="flex items-center gap-3">
        <Building2 className="h-6 w-6 text-teal-700" />
        <h1 className="font-display text-2xl font-bold">{L('Mi Empresa', 'My Company')}</h1>
      </div>

      {/* V5.4 §7 — pending document acknowledgments */}
      <AckInbox />

      {/* ── Celebrations ─────────────────────────────────────── */}
      <section data-testid="me-celebrations">
        <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
          <Cake className="h-4 w-4 text-pink-600" />
          {L(`Celebraciones de ${MONTHS_ES[(celebs?.month || 1) - 1]}`, 'Celebrations this month')}
        </h2>
        {celebs && celebs.birthdays?.length === 0 && (
          <Card className="p-5 text-sm text-muted-foreground" data-testid="me-celebrations-empty">
            {L('No hay cumpleaños compartidos este mes.', 'No shared birthdays this month.')}
          </Card>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {(celebs?.birthdays || []).map((b) => (
            <Card key={b.user_id}
                  className={`p-4 shadow-card transition-all duration-300 hover:shadow-medium ${b.is_today ? 'border-pink-300 bg-gradient-to-br from-pink-50 to-orange-50 animate-in fade-in slide-in-from-bottom-2' : ''}`}
                  data-testid={`me-birthday-${b.user_id}`}>
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${b.is_today ? 'bg-pink-100' : 'bg-slate-100'}`}>
                  {b.is_today ? <PartyPopper className="h-5 w-5 text-pink-600" /> : <Cake className="h-5 w-5 text-slate-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {b.is_today
                      ? L(`¡Hoy cumple años ${b.name}!`, `It's ${b.name}'s birthday today!`)
                      : L(`${b.name} cumple años el ${b.day} de ${MONTHS_ES[b.month - 1]}`, `${b.name}'s birthday is on ${b.date}`)}
                  </p>
                  {b.position && <p className="text-xs text-muted-foreground">{b.position}</p>}
                  {b.favorite_dessert && (
                    <p className="text-xs text-pink-700 mt-1">
                      {L(`Recuerda que su postre favorito es ${b.favorite_dessert}.`, `Remember: their favorite dessert is ${b.favorite_dessert}.`)}
                    </p>
                  )}
                  {!b.is_me && (
                    <Button size="sm" variant="outline" className="mt-2 h-7 text-xs rounded-full"
                            onClick={() => setCongrat(b)} data-testid={`me-congratulate-${b.user_id}`}>
                      <PartyPopper className="h-3.5 w-3.5 mr-1" />
                      {L('Enviar felicitación', 'Send congratulations')}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Communications ───────────────────────────────────── */}
      <section data-testid="me-communications">
        <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
          <Megaphone className="h-4 w-4 text-teal-700" />
          {L('Comunicaciones', 'Communications')}
        </h2>
        {/* CTH-COM-072 — single clean presentation per communication: the
            hero that duplicated the first communication was removed. */}
        {active.length === 0 && (
          <Card className="p-5 text-sm text-muted-foreground" data-testid="me-comms-empty">
            {L('No tienes comunicaciones activas.', 'You have no active communications.')}
          </Card>
        )}
        <div className="space-y-3">
          {active.map((c) => (
            <CommunicationCard key={c.id} c={c} L={L} isES={isES}
              onConfirm={(id) => setComms((p) => p.map((x) => x.id === id ? { ...x, confirmed_at: new Date().toISOString() } : x))} />
          ))}
        </div>
        {history.length > 0 && (
          <div className="mt-3">
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="me-comms-history-toggle">
              <History className="h-4 w-4 mr-1.5" />
              {L(`Historial (${history.length})`, `History (${history.length})`)}
            </Button>
            {showHistory && (
              <div className="space-y-3 mt-2 opacity-75">
                {history.map((c) => (
                  <CommunicationCard key={c.id} c={c} L={L} isES={isES} onConfirm={() => {}} />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Public recognition feed (reused canonical component) ── */}
      {!user?.external_candidate && <MeCompanyRecognitionFeed />}

      <Dialog open={!!congrat} onOpenChange={(o) => !o && setCongrat(null)}>
        <DialogContent data-testid="me-congrats-dialog">
          <DialogHeader>
            <DialogTitle>{L(`Felicitar a ${congrat?.name}`, `Congratulate ${congrat?.name}`)}</DialogTitle>
          </DialogHeader>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500}
                    placeholder={L('Escribe tu mensaje de felicitación…', 'Write your congratulation message…')}
                    data-testid="me-congrats-message" />
          <p className="text-xs text-muted-foreground">{L('Tu mensaje será visible para toda la empresa.', 'Your message will be visible to the whole company.')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCongrat(null)}>{L('Cancelar', 'Cancel')}</Button>
            <Button onClick={sendCongrats} disabled={sending || message.trim().length < 2} data-testid="me-congrats-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              {L('Enviar', 'Send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
